// The venue's guest passes, as the fake api answers them (F-SES-07; spec/05 §3.4; `routes/guest-passes.ts`):
//
//   GET   …/venues/{v}/guest-passes                   listGuestPasses     passes:read
//   POST  …/passes/holdings/{h}/refund                refundPassHolding   passes:refund   (a guest's holding: Void)
//
// The commerce fake mints a guest for each guest pass line a counter sale places (`mint`) and activates it when the sale
// is paid (`activate`), as the server's sale and its payment handler do. With `seeded`, the venue already has guest
// passes in use: one ready, one playing on PC-01, one waiting for its payment — the Guests page's three states.

import type { GuestPass, GuestPassStatus, Money, PassHolding, Problem } from '@clavian/api-client';
import { RefundPassRequestSchema } from '@clavian/contracts';

import type { FakeResponse } from './fake-server';

// Re-declared rather than imported, so this file and `fake-server.ts` do not import each other.
const ORGANIZATION_ID = 'org_01k5znw4hmfay8vvr14d2pf2db';
const VENUE_ID = 'ven_01k5znw4hmfay8vvr14d2pf2dc';
const PLAYER_NIL = 'pla_00000000000000000000000000';
/** PC-01, the one station `fake-server.ts` seeds. */
const STATION_PC01 = 'stn_01k5znw4hmfay8vvr14d2pf3cc';
/** The fixtures' "now", as the commerce fake's. */
const NOW = '2026-09-26T14:00:00.000Z';
const HOUR_MS = 3_600_000;

const ORGANIZATION_PATH = `/v1/organizations/${ORGANIZATION_ID}`;
const GUESTS_PATH = `${ORGANIZATION_PATH}/venues/${VENUE_ID}/guest-passes`;
const REFUND_PATH = new RegExp(`^${ORGANIZATION_PATH}/passes/holdings/([^/]+)/refund$`);

export interface GuestsHelpers {
  readonly json: (status: number, body: unknown, headers?: Record<string, string>) => FakeResponse;
  readonly problem: (status: number, code: string, title: string, extra?: Partial<Problem>) => FakeResponse;
  readonly gate: () => FakeResponse | undefined;
  readonly can: (permission: string) => boolean;
  /** Whether the venue serves guests (`sessions.guestsEnabled`). */
  readonly enabled: () => boolean;
}

const at = (base: string, hours: number): string => new Date(Date.parse(base) + hours * HOUR_MS).toISOString();

/** A ticket code from a serial: six characters of the unambiguous alphabet, the same for the same serial. */
function codeFor(serial: number): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let value = serial * 7_919 + 104_729;
  let code = '';
  for (let index = 0; index < 6; index += 1) {
    code += alphabet[value % alphabet.length];
    value = Math.floor(value / alphabet.length) + index * 13;
  }
  return code;
}

const id = (prefix: string, serial: number): string => `${prefix}_01k5znw4hmfay8vvr14d2g${String(serial).padStart(4, '0')}`.slice(0, prefix.length + 27);

interface GuestRecord {
  pass: GuestPass;
  /** The guest's holding, once paid: what the refund answers. */
  holding: PassHolding | null;
  readonly productId: string;
}

function seeded(): GuestRecord[] {
  const pass = (serial: number, over: Partial<GuestPass>): GuestPass => ({
    id: id('gst', serial),
    code: codeFor(serial),
    status: 'active',
    passName: '3 hours',
    holdingId: id('pah', serial),
    saleId: id('sal', serial),
    price: { amount: 24_000, currency: 'INR' },
    grantedSeconds: 3 * 3600,
    remainingSeconds: 3 * 3600,
    ticketExpiresAt: at(NOW, 22),
    claimableUntil: at(NOW, 166),
    claimedAt: null,
    session: null,
    createdAt: at(NOW, -2),
    ...over,
  });
  const ready = pass(1, { passName: '1 hour', price: { amount: 9_000, currency: 'INR' }, grantedSeconds: 3600, remainingSeconds: 3600, ticketExpiresAt: at(NOW, 23.5), createdAt: at(NOW, -0.5) });
  const playing = pass(2, { remainingSeconds: 2 * 3600 + 14 * 60, session: { id: id('ses', 2), stationId: STATION_PC01 } });
  const waiting = pass(3, { status: 'awaiting_payment', holdingId: null, remainingSeconds: 0, ticketExpiresAt: null, claimableUntil: null, createdAt: NOW });
  return [waiting, ready, playing].map((one) => ({ pass: one, holding: one.holdingId === null ? null : holdingOf(one), productId: id('ppr', 1) }));
}

function holdingOf(pass: GuestPass): PassHolding {
  return {
    id: pass.holdingId!,
    playerId: PLAYER_NIL as PassHolding['playerId'],
    productId: id('ppr', 1) as PassHolding['productId'],
    productVersionId: id('ppv', 1) as PassHolding['productVersionId'],
    name: pass.passName,
    grantedSeconds: pass.grantedSeconds,
    remainingSeconds: pass.remainingSeconds,
    price: pass.price,
    status: 'active',
    purchasedAt: pass.createdAt,
    firstUsedAt: null,
    expiresAt: at(pass.createdAt, 24 * 30),
    scope: { type: 'organization' },
  } as PassHolding;
}

export function createFakeGuests(helpers: GuestsHelpers, options: { readonly seeded?: boolean } = {}) {
  const { json, problem } = helpers;
  const state = {
    records: options.seeded === true ? seeded() : ([] as GuestRecord[]),
    serial: 100,
  };

  const live = (status: GuestPassStatus): boolean => status === 'active' || status === 'awaiting_payment';

  /** A placed sale's guest pass line: a guest and its code, waiting for the payment. */
  function mint(saleId: string, line: { readonly name: string; readonly price: Money; readonly seconds: number; readonly productId: string }): GuestPass {
    state.serial += 1;
    const pass: GuestPass = {
      id: id('gst', state.serial) as GuestPass['id'],
      code: codeFor(state.serial) as GuestPass['code'],
      status: 'awaiting_payment',
      passName: line.name,
      holdingId: null,
      saleId: saleId as GuestPass['saleId'],
      price: line.price,
      grantedSeconds: line.seconds,
      remainingSeconds: 0,
      ticketExpiresAt: null,
      claimableUntil: null,
      claimedAt: null,
      session: null,
      createdAt: NOW,
    };
    state.records.unshift({ pass, holding: null, productId: line.productId });
    return pass;
  }

  /** The sale is paid: each of its guests holds its pass, and its code works for 24 hours. */
  function activate(saleId: string): void {
    for (const record of state.records) {
      if (record.pass.saleId !== saleId || record.pass.status !== 'awaiting_payment') continue;
      state.serial += 1;
      record.pass = {
        ...record.pass,
        status: 'active',
        holdingId: id('pah', state.serial) as GuestPass['holdingId'],
        remainingSeconds: record.pass.grantedSeconds,
        ticketExpiresAt: at(NOW, 24),
        claimableUntil: at(NOW, 24 * 7),
      };
      record.holding = holdingOf(record.pass);
    }
  }

  function handle(method: string, pathname: string, query: URLSearchParams, input: Record<string, unknown>): FakeResponse | undefined {
    if (pathname === GUESTS_PATH) {
      const refused = helpers.gate();
      if (refused !== undefined) return refused;
      if (method !== 'GET') return problem(405, 'method_not_allowed', 'Method not allowed');
      if (!helpers.can('passes:read')) return problem(403, 'forbidden', 'Not allowed');
      const saleId = query.get('saleId');
      const rows = state.records
        .map((record) => record.pass)
        .filter((pass) => (saleId === null ? live(pass.status) || query.get('state') === 'all' : pass.saleId === saleId));
      return json(200, { guestsEnabled: helpers.enabled(), guestPasses: rows });
    }
    const refund = REFUND_PATH.exec(pathname);
    const record = refund === null ? undefined : state.records.find((one) => one.pass.holdingId === refund[1]);
    if (record === undefined || method !== 'POST') return undefined;
    const refused = helpers.gate();
    if (refused !== undefined) return refused;
    if (!helpers.can('passes:refund')) return problem(403, 'forbidden', 'Not allowed');
    const parsed = RefundPassRequestSchema.safeParse(input);
    if (!parsed.success) return problem(400, 'validation_failed', 'Invalid');
    if (record.pass.status !== 'active' || record.holding === null) return problem(409, 'pass_not_refundable', 'The holding cannot be refunded under its rules.');
    record.pass = { ...record.pass, status: 'refunded', remainingSeconds: 0, session: null };
    record.holding = { ...record.holding, status: 'refunded', remainingSeconds: 0 };
    return json(200, record.holding);
  }

  return { state, handle, mint, activate, enabled: helpers.enabled };
}

export type FakeGuests = ReturnType<typeof createFakeGuests>;
